/**
 * The kit box, as data.
 *
 * Extracted from `home-hub-screen.tsx` so the printed kit sheet and the Home
 * Hub read the same table. Two surfaces describing the same physical box must
 * not be able to disagree about how many compartments there are, what order
 * they come in, what each one is called or how long it takes.
 *
 * What stayed behind in `home-hub-screen.tsx` is what only that screen needs:
 * the Phosphor icons, the progressive-tense strings for its active row, and
 * `MODULE_PREREQ_STEPS`.
 */

import type { SystemPresence } from "@/lib/api";

import { MODULE_NAMES } from "./product-names";

/**
 * The five hardware modules, in the enforced install order.
 *
 * This order IS the physical kit box: compartment 1 → 5. The box is the spine
 * of the whole install, so the rows, the order badges, every "from compartment
 * N" line in a module's copy and the printed sheet all have to agree with it.
 * Note this deliberately differs from the backend's FLOW_STEPS order (which has
 * heating and ventilation before windows): step keys are persisted and stay
 * stable, while the order the user walks them in is a product decision that
 * lives here.
 *
 * On the literal "compartment N" strings throughout the copy: hardcoded
 * numbers are correct for the beta and should not be refactored into derived
 * values. Every beta home receives the same five compartments and the boxes
 * carry big number stickers, so copy must mirror the physical label exactly.
 * Post-beta the kit goes modular (some homes get three compartments, some
 * five) and numbers stop making sense; the plan is named compartments instead
 * ("the heating compartment", "the ventilation compartment", ...). When that
 * lands, every compartment reference in copy changes together as one pass.
 */
export const MODULE_KEYS = [
  "home_assistant", // compartment 1 · SmartClimate Hub
  "air_sensors", // compartment 2 · Air Quality
  "windows", // compartment 3 · VELUX Windows
  "floor_heating", // compartment 4 · Heating
  "ventilation", // compartment 5 · Ventilation
] as const;

export type ModuleKey = (typeof MODULE_KEYS)[number];

export interface CompartmentMeta {
  /** Canonical module name, from MODULE_NAMES so a rename is one edit. */
  label: string;
  minutes: number;
  /** Hero product shot, under `frontend/public`. */
  image: string;
}

/**
 * Per-compartment content — Figma 1175:1224.
 *
 * `minutes` is an estimate for someone who has the box open and the app in
 * hand. The Home Hub sums these for its "how long is left" copy and the kit
 * sheet sums them for its total, so neither writes a time down. The sheet adds
 * `CLOSING_TASK.minutes` onto Air Quality, which is where that work belongs on
 * paper.
 *
 * There is deliberately no imperative verb here. The Home Hub builds its active
 * row from `MODULE_PROGRESSIVE`, which is local to that screen, and the kit
 * sheet names compartments rather than actions, so a shared `verb` had no
 * consumer and only invited a third phrasing of the same five modules.
 */
export const CARD_META: Record<ModuleKey, CompartmentMeta> = {
  home_assistant: {
    label: MODULE_NAMES.home_assistant!,
    minutes: 5,
    image: "/brand/onboarding/hub-device.png",
  },
  air_sensors: {
    label: MODULE_NAMES.air_sensors!,
    minutes: 5,
    image: "/brand/onboarding/airquality-connector.png",
  },
  windows: {
    label: MODULE_NAMES.windows!,
    minutes: 15,
    // The top-down shot, not `windows-connector.png`: that one is a white
    // face-on cutout with only 1.2% non-white pixels and disappears at 96×56.
    image: "/brand/onboarding/windows-connector-top.png",
  },
  floor_heating: {
    label: MODULE_NAMES.floor_heating!,
    minutes: 15,
    image: "/brand/onboarding/heating-connector.png",
  },
  ventilation: {
    label: MODULE_NAMES.ventilation!,
    minutes: 5,
    // The black USR gateway, same as heating: that is what is physically in
    // the compartment. The NILAN shot is the household's own unit, which they
    // will not find in the box.
    image: "/brand/onboarding/heating-connector.png",
  },
};

/**
 * The closing task: placing the air sensors, once every compartment is done.
 *
 * Deliberately NOT compartment 6. There is no sixth section in the box, and the
 * compartment numbers have to keep mapping 1:1 onto the physical thing or the
 * whole vocabulary stops meaning anything. It is the last beat of the install
 * instead, which is also a better ending than a bare "Finish setup" button.
 *
 * It only applies to households that have the air-quality system at all.
 */
export const CLOSING_TASK = { label: "Place your air sensors", minutes: 15 } as const;

/**
 * Which `preconfig.systems` flag gates which compartment.
 *
 * Mirrors `STEP_SYSTEM` in `backend/.../flow.py`. Keys absent here always
 * apply: every household has a hub. The two must stay in step, or the sheet
 * prints a compartment the flow will never offer.
 */
const MODULE_SYSTEM: Partial<Record<ModuleKey, keyof SystemPresence>> = {
  air_sensors: "air_quality",
  windows: "roof_windows",
  floor_heating: "floor_heating",
  ventilation: "ventilation",
};

/**
 * The compartments a household actually has, in box order.
 *
 * A missing flag counts as installed, matching `SystemPresence`'s server-side
 * defaults, so households predating the Systems card keep every compartment.
 *
 * @param systems The household's `preconfig.systems` flags.
 * @returns The applicable module keys, in `MODULE_KEYS` order.
 */
export function applicableCompartments(
  systems: SystemPresence | null | undefined,
): readonly ModuleKey[] {
  if (!systems) {
    return MODULE_KEYS;
  }
  return MODULE_KEYS.filter((key) => {
    const flag = MODULE_SYSTEM[key];
    return flag === undefined || systems[flag] !== false;
  });
}
